import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { CreateUserComponent } from '../create-user/create-user.component';
import { DialogModule } from 'primeng/dialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { PropertyService } from '../properties/service/property.service';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [FormsModule, CommonModule, TableModule, ButtonModule, CreateUserComponent, DialogModule, ConfirmDialogModule, ToastModule, DynamicDialogModule],
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class UsersComponent {
  users: any[] = [];
  totalRecords = 0;
  currentPage = 0;
  itemsPerPage = 10;
  position: 'center' | 'right' = 'center';
  visible: boolean = false;
  selectedUser: any | null = null;
  blockSpecialCharsPattern = /[.*+?^${}()|[\]\\]/g;

  // Filters
  filters = {
    search: '',
  };
  sortField: string | null = null;
  sortOrder: number = 1;

  constructor(
    private router: Router,
    private propertyService: PropertyService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) { }

  showDialogForEdit(position: 'right', user: any): void {
    this.position = position;
    this.selectedUser = user;
    this.visible = true;
  }

  showDialogForCreate(position: 'right'): void {
    this.position = position;
    this.selectedUser = null;
    this.visible = true;
  }

  onDialogClose(): void {
    this.visible = false;
    this.selectedUser = null;
    this.fetchUsers({ first: this.currentPage * this.itemsPerPage, rows: this.itemsPerPage });
  }

  fetchUsers(event: any): void {
    const page = event.first / event.rows + 1;
    const limit = event.rows;

    const params: any = {
      page: page,
      limit: limit,
      search: this.filters.search,
      sortField: event.sortField,
      sortOrder: event.sortOrder,
    };

    this.propertyService.getAdminUsers(params).subscribe({
      next: (response) => {
        this.users = response.data;
        this.totalRecords = response.total;
        this.currentPage = page - 1;
      },
      error: (err) => {
        console.error('Error fetching users:', err);
      },
    });
  }

  onFilterChange(): void {
    this.fetchUsers({ first: this.currentPage * this.itemsPerPage, rows: this.itemsPerPage });
  }

  userIndex(user: any): number {
    return this.currentPage * 10 + this.users.indexOf(user) + 1;
  }

  deleteUser(id: string): void {
    this.propertyService.deteleUserOrTenant(id).subscribe({
      next: () => {
        this.users = this.users.filter(user => user._id !== id);
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'User deleted successfully' });
      },
      error: (err) => {
        console.error('Error deleting user:', err);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete user' });
      },
    });
  }

  confirmDelete(userId: string) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this user?',
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle text-danger',
      accept: () => {
        this.deleteUser(userId);
      },
      reject: () => {
        this.messageService.add({ severity: 'info', summary: 'Cancelled', detail: 'User deletion cancelled' });
      },
    });
  }

  // Block on typing
  blockSpecialChars(event: KeyboardEvent) {
    console.log("BLOCK typing");
    if (this.blockSpecialCharsPattern.test(event.key)) {
      event.preventDefault();
    }
  }

  // Block on paste
  blockSpecialCharsOnPaste(event: ClipboardEvent) {
    console.log("BLOCK PASTE");
    const pasteData = event.clipboardData?.getData('text') || '';
    if (this.blockSpecialCharsPattern.test(pasteData)) {
      event.preventDefault();
    }
  }

  // Block on drop
  blockSpecialCharsOnDrop(event: DragEvent) {
    console.log("BLOCK Drop");
    const dropData = event.dataTransfer?.getData('text') || '';
    if (this.blockSpecialCharsPattern.test(dropData)) {
      event.preventDefault();
    }
  }

  // Final cleanup in case something sneaks in
  sanitizeInput(event: Event) {
    console.log("SANITIZE INPUT");
    const input = event.target as HTMLInputElement;
    if (this.blockSpecialCharsPattern.test(input.value)) {
      input.value = input.value.replace(this.blockSpecialCharsPattern, '');
    }
  }

}
